import { FieldState, FormState } from 'formstate';
import { action, computed, makeObservable, observable, ObservableMap, runInAction } from 'mobx';
import {
    PartnerInput,
    PartnerConfig,
    IPartnerDetail,
    TenantManagementApi,
} from '@servicetitan/marketing-tenant-management-api';
import { inject, injectable } from '@servicetitan/react-ioc';
import {
    formStateToJS,
    InputFieldState,
    setFormStateValues,
    commitFormState,
} from '@servicetitan/form-state';
import { LoadStatus } from '../../common/enums/load-status';
import { withTrimmedValues } from '../utils/with-trimmed-values';
import { REQUIRED_CONFIGURATION_VALIDATORS } from '../utils/validators';
import { sortFeatureGates } from '../utils/sorters';

@injectable()
export class ManagePartnerStore {
    formState = new FormState({
        name: new InputFieldState('').validators(...REQUIRED_CONFIGURATION_VALIDATORS),
        emailScope: new InputFieldState('').validators(...REQUIRED_CONFIGURATION_VALIDATORS),
        emailSenderDomain: new InputFieldState('').validators(...REQUIRED_CONFIGURATION_VALIDATORS),
        directMailProvider: new InputFieldState('').validators(
            ...REQUIRED_CONFIGURATION_VALIDATORS,
        ),
        partnerKBUrl: new InputFieldState('').validators(...REQUIRED_CONFIGURATION_VALIDATORS),
        authPath: new InputFieldState(''),
        emailTemplateOptOut: new FieldState(false),
        supportCallForwarding: new FieldState(true),
        managedDomains: new FieldState<string[]>([]),
    });

    @observable partner?: IPartnerDetail;
    @observable pageLoadStatus = LoadStatus.None;
    @observable saveLoadStatus = LoadStatus.None;

    partnerGenericConfigs: ObservableMap<string> = observable.map([], { deep: false });

    @computed get isFormChanged() {
        return (
            this.formState.$.name.dirty ||
            this.formState.$.emailScope.dirty ||
            this.formState.$.emailSenderDomain.dirty ||
            this.formState.$.directMailProvider.dirty ||
            this.formState.$.authPath.dirty ||
            this.formState.$.partnerKBUrl.dirty ||
            this.formState.$.emailTemplateOptOut.dirty ||
            this.formState.$.supportCallForwarding.dirty ||
            this.formState.$.managedDomains.dirty
        );
    }

    constructor(
        @inject(TenantManagementApi) private readonly tenantManagementApi: TenantManagementApi,
    ) {
        makeObservable(this);
    }

    init = async (externalId: string) => {
        this.formState.disableAutoValidation();

        runInAction(() => {
            this.pageLoadStatus = LoadStatus.Loading;
        });

        try {
            const { data: partner } = await this.tenantManagementApi.partner(externalId);

            const {
                displayName,
                configs: {
                    authPath,
                    emailScope,
                    partnerKBUrl,
                    emailSenderDomain,
                    directMailProvider,
                    isPartnerLevelAuth,
                    emailTemplateOptOut,
                    supportCallForwarding,
                    managedDomains,
                    generic: partnerGenericConfigs,
                },
            } = partner;

            setFormStateValues(this.formState, {
                name: displayName,
                authPath: authPath || '',
                emailScope,
                partnerKBUrl: partnerKBUrl || undefined,
                emailSenderDomain,
                directMailProvider,
                emailTemplateOptOut: emailTemplateOptOut ?? false,
                supportCallForwarding: supportCallForwarding ?? true,
                managedDomains: managedDomains ?? [],
            });

            if (isPartnerLevelAuth) {
                this.formState.$.authPath.validators(...REQUIRED_CONFIGURATION_VALIDATORS);
            }

            commitFormState(this.formState);

            runInAction(() => {
                this.partner = partner;
                this.partnerGenericConfigs.replace(sortFeatureGates(partnerGenericConfigs));
                this.pageLoadStatus = LoadStatus.Ok;
            });
        } catch {
            runInAction(() => {
                this.pageLoadStatus = LoadStatus.Error;
            });
        }
    };

    saveConfigs = async () => {
        if (!this.partner || this.saveLoadStatus === LoadStatus.Loading) {
            return;
        }

        try {
            await this.formState.enableAutoValidationAndValidate();

            if (this.formState.hasError) {
                return;
            }

            runInAction(() => {
                this.saveLoadStatus = LoadStatus.Loading;
            });

            const {
                name,
                authPath,
                emailScope,
                partnerKBUrl,
                emailSenderDomain,
                directMailProvider,
                emailTemplateOptOut,
                supportCallForwarding,
                managedDomains,
            } = formStateToJS(this.formState);

            const configs = {
                ...this.partner.configs,
                emailScope,
                partnerKBUrl,
                emailSenderDomain,
                directMailProvider,
                emailTemplateOptOut,
                supportCallForwarding,
                managedDomains,
                generic: {}, // generic configs are not saved by this BE method
            };

            if (this.partner.configs.isPartnerLevelAuth) {
                configs.authPath = authPath;
            }

            const partnerDetails = new PartnerInput({
                name,
                configs: new PartnerConfig(configs),
            });

            const body = withTrimmedValues(partnerDetails);

            await this.tenantManagementApi.configPUT2(this.partner.internalId, body);

            if (this.partner.displayName !== body.name) {
                this.partner.displayName = body.name;
            }

            commitFormState(this.formState);

            runInAction(() => {
                this.saveLoadStatus = LoadStatus.Ok;
            });
        } catch {
            runInAction(() => {
                this.saveLoadStatus = LoadStatus.Error;
            });
        }
    };

    @action resetConfiguration = () => {
        this.formState.reset();
        this.saveLoadStatus = LoadStatus.None;
    };
}
